import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Inbox, Bot, Users, Boxes, Clock, ShieldCheck, MessageCircle, Workflow } from "lucide-react";
import PageHero from "../components/site/PageHero";
import RespondIO from "../components/site/RespondIO";
import FinalCTA from "../components/site/FinalCTA";
import { usePageSEO, breadcrumb, serviceSchema } from "../hooks/usePageSEO";
import { BRAND, waLink } from "../brand";

const benefits = [
  { icon: Inbox, t: "Bandeja unificada", d: "WhatsApp Business API, Instagram DM, Facebook Messenger, web chat, email y SMS en una sola pantalla." },
  { icon: Bot, t: "IA conversacional nativa", d: "Agentes IA + reglas de negocio + escalamiento humano cuando hace falta." },
  { icon: Users, t: "Atribución de ventas", d: "Cada conversación atada a campañas y agentes para medir ROAS real." },
  { icon: Boxes, t: "Integraciones empresariales", d: "Webhooks, CRM, ecommerce, billing y APIs internas. Production-ready." },
  { icon: Clock, t: "Atención 24/7", d: "Tu equipo deja de cargar el peso de responder fuera de horario." },
  { icon: ShieldCheck, t: "Cumplimiento empresarial", d: "Roles, permisos, trazabilidad y compliance que enterprise necesita." },
];

const integrations = [
  "WhatsApp Business API", "Instagram", "Facebook Messenger", "Web Chat", "Email",
  "Google Sheets", "HubSpot", "Pipedrive", "Salesforce", "Notion",
  "Make", "n8n", "Zapier", "Webhooks", "OpenAI / Claude",
];

const phases = [
  { n: "01", t: "Auditoría operativa", d: "Reviso tus canales, equipo, herramientas actuales y dolor real." },
  { n: "02", t: "Diseño del workspace", d: "Arquitectura de canales, roles, contactos, etiquetas, flujos y SLAs." },
  { n: "03", t: "Implementación técnica", d: "Configuración, integraciones, IA, automatizaciones y plantillas." },
  { n: "04", t: "Entrenamiento + handoff", d: "Tu equipo queda operando. Reportes, KPIs y soporte continuo." },
];

export default function RespondIOPage() {
  usePageSEO({
    title: "Implementación Respond.io en Guatemala y LATAM | Daos Ads",
    description:
      "Implementación experta de Respond.io: centralización omnicanal (WhatsApp Business API, Instagram, Facebook, Web Chat, Email), IA conversacional, integraciones empresariales y entrenamiento del equipo. Partner de implementación.",
    path: "/respond-io",
    jsonLd: [
      breadcrumb([
        { name: "Inicio", path: "/" },
        { name: "Respond.io", path: "/respond-io" },
      ]),
      serviceSchema({
        name: "Implementación de Respond.io",
        description:
          "Implementación extremo a extremo de Respond.io con IA conversacional, integraciones CRM y entrenamiento del equipo comercial.",
        path: "/respond-io",
      }),
    ],
  });

  return (
    <main>
      <PageHero
        eyebrow="Partner de implementación"
        title="Centraliza tus canales y"
        highlight="deja de perder leads"
        description="Implemento Respond.io de extremo a extremo: integraciones, automatizaciones, IA conversacional, reportes y entrenamiento al equipo. Tu operación deja de depender de cinco bandejas distintas."
        primaryCta={{ to: "/contacto", label: "Quiero implementarlo" }}
      />

      {/* Existing Respond.io block (mockup + content) */}
      <RespondIO />

      {/* Benefits grid */}
      <section className="relative py-20">
        <div className="container-x">
          <div className="reveal max-w-2xl">
            <div className="eyebrow mb-5">Beneficios concretos</div>
            <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
              Lo que <span className="text-gradient">cambia en tu operación</span> el día 1.
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map(({ icon: Icon, t, d }, i) => (
              <div key={t} className="reveal grad-border p-6 card-lift" style={{ transitionDelay: `${i * 50}ms` }}>
                <div className="w-11 h-11 rounded-xl bg-gradient-brand grid place-items-center text-black/90 mb-5">
                  <Icon className="w-5 h-5" strokeWidth={2.4} />
                </div>
                <div className="text-white font-display font-semibold">{t}</div>
                <p className="mt-2 text-zinc-400 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation phases */}
      <section className="relative py-20">
        <div className="container-x">
          <div className="reveal max-w-2xl">
            <div className="eyebrow mb-5">Cómo lo implemento</div>
            <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
              Cuatro fases para dejar tu operación <span className="text-gradient">enterprise-ready</span>.
            </h2>
          </div>
          <div className="mt-12 hidden lg:block relative">
            <div className="absolute top-12 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff8a00]/40 to-transparent" />
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {phases.map((s, i) => (
              <div key={s.n} className="reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="w-24 h-24 rounded-full bg-[#0a0a0a] border border-white/10 grid place-items-center mx-auto lg:mx-0 mb-6 relative">
                  <div className="absolute inset-0 rounded-full bg-gradient-brand opacity-15 blur-md" />
                  <span className="font-display font-extrabold text-3xl text-gradient relative">{s.n}</span>
                </div>
                <h3 className="font-display font-bold text-white text-xl tracking-tight">{s.t}</h3>
                <p className="mt-2.5 text-zinc-400 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="relative py-20">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 reveal">
              <div className="eyebrow mb-5">Integraciones</div>
              <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
                Conectado a <span className="text-gradient">todo tu stack</span>.
              </h2>
              <p className="mt-5 text-zinc-400 max-w-md">
                Si expone API, webhook o tiene integración nativa, lo conecto a tu workspace. Sin parches, sin Frankenstein.
              </p>
            </div>
            <div className="lg:col-span-7 reveal flex flex-wrap gap-2.5">
              {integrations.map((it) => (
                <span
                  key={it}
                  className="px-4 py-2 rounded-full glass text-sm text-zinc-200 hover:border-[#ff8a00]/40 transition"
                >
                  {it}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Affiliate / final CTA */}
      <section className="relative py-20">
        <div className="container-x">
          <div className="reveal grad-border p-10 sm:p-14 relative overflow-hidden">
            <div className="absolute -top-32 -right-20 w-[520px] h-[520px] bg-gradient-to-br from-[#ff3b1f]/25 via-[#ff8a00]/20 to-transparent rounded-full blur-3xl" />
            <div className="grid lg:grid-cols-12 gap-10 items-center relative">
              <div className="lg:col-span-7">
                <div className="eyebrow mb-4">Conoce Respond.io</div>
                <h2 className="font-display font-extrabold text-white text-3xl sm:text-5xl tracking-tighter leading-[1.05]">
                  Crea tu cuenta y empieza a <span className="text-gradient">centralizar canales</span> hoy.
                </h2>
                <p className="mt-5 text-zinc-400 max-w-2xl">
                  Si quieres conocer la plataforma por tu cuenta antes de hablar conmigo, créala con mi enlace de partner. Si después decides implementarla en serio, te ayudo a operarla.
                </p>
              </div>
              <div className="lg:col-span-5 flex flex-col gap-3">
                <a
                  href={BRAND.respondAffiliate}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary w-full justify-center"
                  data-testid="respond-page-affiliate-cta"
                >
                  <MessageCircle className="w-4 h-4" />
                  Conocer Respond.io
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link to="/contacto" className="btn-secondary w-full justify-center" data-testid="respond-page-implementation-cta">
                  Quiero que lo implementes
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="relative py-20">
        <div className="container-x">
          <div className="reveal max-w-2xl">
            <div className="eyebrow mb-5">Continúa explorando</div>
            <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
              Servicios <span className="text-gradient">conectados</span>.
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { to: "/automatizacion-ia", label: "Automatización con IA", desc: "Agentes y flujos que multiplican el efecto de Respond.io." },
              { to: "/funnels", label: "Funnels", desc: "Sistema completo de adquisición conectado a la bandeja." },
              { to: "/casos-de-exito", label: "Casos de éxito", desc: "Resultados reales tras implementar Respond.io." },
            ].map((r) => (
              <Link key={r.to} to={r.to} className="reveal grad-border p-6 card-lift block">
                <div className="flex items-center justify-between">
                  <div className="text-white font-display font-semibold">{r.label}</div>
                  <ArrowUpRight className="w-5 h-5 text-zinc-400" />
                </div>
                <p className="mt-2 text-zinc-400 text-sm">{r.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
}
